// Contracts — Mini App менеджера пайплайна (хендофф «Dev (Денис) v2»).
//
// Вход у всех ручек один: `initData` от Telegram. Это подписанная строка, и
// она же — единственное удостоверение личности: воркер проверяет подпись
// бот-токеном и по user_id находит человека в `tg_people`. Никаких токенов в
// URL и никаких «ну он же открыл ссылку».
import { z } from 'zod';

export const TgAppReqSchema = z.object({
  initData: z.string().min(1).max(4096),
});
export type TgAppReq = z.infer<typeof TgAppReqSchema>;

export const TgAppActivityReqSchema = TgAppReqSchema.extend({
  range: z.enum(['day', 'week', 'all']).default('day'),
});
export type TgAppActivityReq = z.infer<typeof TgAppActivityReqSchema>;

export const TgAppRemindReqSchema = TgAppReqSchema.extend({
  card_id: z.string().trim().min(1).max(64),
});
export type TgAppRemindReq = z.infer<typeof TgAppRemindReqSchema>;

// ── пайплайн ────────────────────────────────────────────────────────────────

/** Этап в терминах интерфейса; внутренние этапы карточки схлопываются в эти. */
export const TgAppStageSchema = z.enum(['research', 'design', 'manifest', 'multiply', 'qa']);
export type TgAppStage = z.infer<typeof TgAppStageSchema>;

export const TgAppCatSchema = z.enum(['gift', 'coupon', 'ticket', 'member']);
export const TgAppKindSchema = z.enum(['print', 'shot']);

export const TgAppErrSchema = z.object({
  who: z.string(),          // имя автора этапа-виновника ('' если неизвестен)
  st: z.string(),           // этап, на котором ошибка возникла
  text: z.string(),         // короткая формулировка (plain, иначе исходный текст)
  ago: z.string(),          // «40 мин назад»
  ai: z.boolean(),          // нашла модель, а не человек → бейдж AI
});

export const TgAppChainSchema = z.object({
  who: z.string(),
  st: z.string(),
  took: z.string(),         // «2 ч 10 мин» либо «—», если время неизвестно
  ago: z.string(),
  bad: z.boolean(),         // на этом этапе есть замечание
});

export const TgAppCardSchema = z.object({
  id: z.string(),
  title: z.string(),        // «gogift · гифткарты»
  locale: z.string(),
  cat: TgAppCatSchema,
  kind: TgAppKindSchema,
  stage: TgAppStageSchema,
  now: z.object({
    who: z.string(),        // '' = этап без исполнителя (QA)
    for_: z.string(),       // сколько уже идёт; '' если неизвестно
    doneN: z.number().int().nonnegative().optional(),
    of: z.number().int().nonnegative().optional(),
    slow: z.boolean().default(false),
  }),
  errs: z.array(TgAppErrSchema),
  chain: z.array(TgAppChainSchema),
});
export type TgAppCard = z.infer<typeof TgAppCardSchema>;

export const TgAppDoneSchema = z.object({
  cards: z.number().int().nonnegative(),
  packs: z.number().int().nonnegative(),
});

export const TgAppPipelineResSchema = z.object({
  ok: z.literal(true),
  who: z.string(),          // как подписать шапку: «Денис · dev-вид»
  done: z.object({ today: TgAppDoneSchema, week: TgAppDoneSchema }),
  cards: z.array(TgAppCardSchema),
  /** Помечает, что времени на этапе у части карточек нет: лог ведётся с 12.08. */
  partial_history: z.boolean(),
});
export type TgAppPipelineRes = z.infer<typeof TgAppPipelineResSchema>;

// ── активность ──────────────────────────────────────────────────────────────

export const TgAppPersonStatSchema = z.object({
  name: z.string(),
  dept: z.string(),
  units: z.number().int().nonnegative(),
  avg_minutes: z.number().int().nonnegative().nullable(),
  err_pct: z.number().nonnegative(),
});

export const TgAppActivityResSchema = z.object({
  ok: z.literal(true),
  range: z.enum(['day', 'week', 'all']),
  team: z.object({
    units: z.number().int().nonnegative(),
    avg_minutes: z.number().int().nonnegative().nullable(),
    err_pct: z.number().nonnegative(),
  }),
  people: z.array(TgAppPersonStatSchema),
  partial_history: z.boolean(),
});
export type TgAppActivityRes = z.infer<typeof TgAppActivityResSchema>;

export const TgAppRemindResSchema = z.object({
  ok: z.boolean(),
  /** Кого дёрнули — для тоста. */
  target: z.string(),
  /** true, если сообщение легло в очередь, но замок инициативных его придержал. */
  held: z.boolean(),
});
export type TgAppRemindRes = z.infer<typeof TgAppRemindResSchema>;
